/**
 * procedureSummaryProjection — specialty-NEUTRAL per-procedure projection for
 * the generic Procedure Summary (doctors without a chart). Unlike the dental
 * `summaryProjection` (keyed by tooth tokens, which drops non-tooth procedures),
 * this is a flat per-procedure list bucketed by status.
 *
 * TODO(neutral-refactor): this + the dental per-tooth projection should share a
 * common per-procedure core when the pluggable-chart refactor lands.
 */
import {
  CANCELLED_STATUSES,
  DONE_STATUSES,
  IN_PROGRESS_STATUSES,
} from './dental/summaryHelpers';

// Procedure buckets (no "open" — that's a dental finding concept). Everything
// non-terminal/non-cancelled that isn't in-progress is "planned" (incl.
// awaiting_payment / suggested), matching the dental decision to fold "proposed"
// into Planned.
export const PROCEDURE_PILLS = ['planned', 'in_progress', 'done', 'cancelled'];

export function deriveProcedureBucket(status) {
  const s = String(status || '').toLowerCase();
  if (DONE_STATUSES.has(s)) return 'done';
  if (IN_PROGRESS_STATUSES.has(s)) return 'in_progress';
  if (CANCELLED_STATUSES.has(s)) return 'cancelled';
  return 'planned';
}

// Best lifecycle date for newest-first ordering.
function procDate(s) {
  return s?.completed_at || s?.executed_at || s?.payment_cleared_at
    || s?.cancelled_at || s?.created_at || s?.scheduled_for || '';
}

/**
 * Map a raw sitting row → the `plan` shape RowActionToolbar / ProcedureViewPopover
 * (and the row renderer) expect, plus a `bucket` for filtering.
 */
function toRow(s) {
  const finalCost = s?.final_cost != null ? Number(s.final_cost) : null;
  return {
    bucket: deriveProcedureBucket(s?.status),
    // RowActionToolbar / popover `plan` shape:
    sittingId: s?.sitting_id || null,
    planId: s?.plan_id || null,
    visitId: s?.visit_id || null,
    status: String(s?.status || '').toLowerCase(),
    name: s?.procedure_name || s?.procedure_snapshot?.name || 'Procedure',
    assignee: s?.assigned_to_name || null,
    assignedToMemberId: s?.assigned_to_member_id || null,
    originDoctor: s?.origin_doctor_name || null,
    cancelledBy: s?.cancelled_by_name || null,
    expiresAt: s?.expires_at || null,
    standing: s?.standing ?? null,
    finalCost: finalCost != null && !Number.isNaN(finalCost) && finalCost > 0
      ? finalCost : null,
    created_at: s?.created_at || null,
    executed_at: s?.executed_at || null,
    completed_at: s?.completed_at || null,
    payment_cleared_at: s?.payment_cleared_at || null,
    startedAt: s?.executed_at || s?.created_at || null,
  };
}

/**
 * buildProcedureSummary(sittings) → { rows (newest-first), counts }.
 * Dedupes by sitting_id (the bundle can carry a sitting under multiple visit
 * keys) and skips rows without a sitting_id.
 */
export function buildProcedureSummary(sittings = []) {
  const seen = new Set();
  const rows = [];
  for (const s of sittings) {
    const id = s?.sitting_id;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    rows.push(toRow(s));
  }
  rows.sort((a, b) => String(procDate(b)).localeCompare(String(procDate(a))));

  const counts = { all: rows.length, planned: 0, in_progress: 0, done: 0, cancelled: 0 };
  for (const r of rows) counts[r.bucket] = (counts[r.bucket] || 0) + 1;

  return { rows, counts };
}
